import { TOGGLER } from "./enums";

const TOGGLE_ROUTE_CURRENT_PATH_KEY = "vAnalytics_toggleRouteCurrentPath";
const TOGGLE_ROUTE_INTENT_KEY = "vAnalytics_toggleRouteIntent";

const APPLICATION_ROUTE = /^(?:\/analytics\/v4)?\/applications\/?$/;
const APP360_ROUTE = /^(?:\/analytics\/v4)?\/dashboards\/(?!dashboard\/?$)[^/]+\/?$/;

export const APPLICATION_TOGGLE_CONFIG = Object.freeze({
  key: "vAnalytics_applicationToggleView",
  allowedValues: [TOGGLER.CARD, TOGGLER.LIST],
  defaultValue: TOGGLER.CARD,
});

export const APP360_TOGGLE_CONFIG = Object.freeze({
  key: "vAnalytics_app360ToggleView",
  allowedValues: [TOGGLER.CARD, TOGGLER.MAP, TOGGLER.LIST],
  defaultValue: TOGGLER.MAP,
});

/*
 * Returns the provided pathname, or falls back to the browser pathname.
 * Example input: pathname="/analytics/v4/applications"
 * Example output: "/analytics/v4/applications"
 */
const getPathname = (pathname = "") => {
  if (pathname) return pathname;
  if (typeof window === "undefined") return "";
  return window.location.pathname;
};

/*
 * Normalizes a route so comparisons are case-insensitive and slash-safe.
 * Example input: pathname="/Analytics//v4/Applications/"
 * Example output: "/analytics/v4/applications"
 */
export const normalizeTogglePath = (pathname = "") =>
  getPathname(pathname).toLowerCase().replace(/\/{2,}/g, "/").replace(/\/$/, "");

/*
 * Returns the browser localStorage object when window is available.
 * Example input: none
 * Example output: window.localStorage or null
 */
const getLocalStorage = () =>
  typeof window === "undefined" ? null : window.localStorage;

/*
 * Returns the browser sessionStorage object when window is available.
 * Example input: none
 * Example output: window.sessionStorage or null
 */
const getSessionStorage = () =>
  typeof window === "undefined" ? null : window.sessionStorage;

/*
 * Checks whether a pathname points to the Applications landing route.
 * Example input: pathname="/analytics/v4/applications"
 * Example output: true
 */
export const isApplicationsPath = (pathname = "") =>
  APPLICATION_ROUTE.test(normalizeTogglePath(pathname));

/*
 * Checks whether a pathname points to an App360 route.
 * Example input: pathname="/analytics/v4/dashboards/Webex"
 * Example output: true
 */
export const isApp360Path = (pathname = "") =>
  APP360_ROUTE.test(normalizeTogglePath(pathname));

/*
 * Checks whether the Application MFE owns state for the supplied route.
 * Keep this predicate centralized so host and reducer route handling cannot drift.
 */
export const isApplicationMFERoute = (pathname = "") =>
  isApplicationsPath(pathname) || isApp360Path(pathname);

/*
 * Extracts the dashboard name segment from an App360 route.
 * Example input: pathname="/analytics/v4/dashboards/Webex"
 * Example output: "Webex"
 */
export const getApp360Name = (pathname = "") => {
  const segments = normalizeTogglePath(pathname).split("/").filter(Boolean);
  const dashboardsIndex = segments.findIndex((segment) => segment === "dashboards");

  if (dashboardsIndex < 0 || dashboardsIndex === segments.length - 1) {
    return "";
  }

  return decodeURIComponent(segments[dashboardsIndex + 1]);
};

/*
 * Returns the toggle storage config that matches the pathname type.
 * Example input: pathname="/analytics/v4/dashboards/Webex"
 * Example output: APP360_TOGGLE_CONFIG
 */
export const getToggleConfig = (pathname = "") =>
  isApp360Path(pathname) ? APP360_TOGGLE_CONFIG : APPLICATION_TOGGLE_CONFIG;

/*
 * Reads the saved toggle view for a route, or falls back to that route's default.
 * Example input: pathname="/analytics/v4/applications"
 * Example output: "card"
 */
export const getStoredToggleView = (pathname = "") => {
  const config = getToggleConfig(pathname);
  const storedValue = getLocalStorage()?.getItem(config.key);

  return config.allowedValues.includes(storedValue)
    ? storedValue
    : config.defaultValue;
};

/*
 * Persists a valid toggle view for the route type in localStorage.
 * Example input: pathname="/analytics/v4/dashboards/Webex", toggleView="map"
 * Example output: no return value; localStorage["vAnalytics_app360ToggleView"] = "map"
 */
export const storeToggleView = (pathname = "", toggleView) => {
  const config = getToggleConfig(pathname);

  if (config.allowedValues.includes(toggleView)) {
    getLocalStorage()?.setItem(config.key, toggleView);
  }
};

/*
 * Reads the last route committed for toggle-navigation tracking.
 * Example input: none
 * Example output: "/analytics/v4/applications"
 */
export const getTrackedToggleCurrentPath = () =>
  getSessionStorage()?.getItem(TOGGLE_ROUTE_CURRENT_PATH_KEY) || "";

/*
 * Stores the current normalized route used to resolve pending toggle navigation.
 * Example input: pathname="/analytics/v4/applications/"
 * Example output: no return value; sessionStorage current path becomes "/analytics/v4/applications"
 */
const setTrackedToggleCurrentPath = (pathname = "") => {
  const normalizedPath = normalizeTogglePath(pathname);

  if (!normalizedPath) return;

  getSessionStorage()?.setItem(TOGGLE_ROUTE_CURRENT_PATH_KEY, normalizedPath);
};

/*
 * Reads the pending navigation intent object from sessionStorage.
 * Example input: none
 * Example output: { pathname: "/analytics/v4/dashboards/webex", toggleView: "map" } or null
 */
const getNavigationToggleIntent = () => {
  const rawIntent = getSessionStorage()?.getItem(TOGGLE_ROUTE_INTENT_KEY);

  if (!rawIntent) return null;

  try {
    const parsedIntent = JSON.parse(rawIntent);
    return parsedIntent?.pathname && parsedIntent?.toggleView
      ? parsedIntent
      : null;
  } catch (error) {
    return null;
  }
};

/*
 * Saves the toggle view that should be applied after navigation reaches a route.
 * Example input: pathname="/analytics/v4/dashboards/Webex", toggleView="map"
 * Example output: no return value; sessionStorage intent is updated with the normalized path
 */
const setNavigationToggleIntent = (pathname = "", toggleView) => {
  const normalizedPath = normalizeTogglePath(pathname);

  if (!normalizedPath || !toggleView) return;

  getSessionStorage()?.setItem(
    TOGGLE_ROUTE_INTENT_KEY,
    JSON.stringify({
      pathname: normalizedPath,
      toggleView,
    }),
  );
};

/*
 * Clears the pending navigation intent, either unconditionally or for a matching route.
 * Example input: pathname="/analytics/v4/dashboards/webex"
 * Example output: no return value; matching sessionStorage intent is removed
 */
const clearNavigationToggleIntent = (pathname = "") => {
  const navigationIntent = getNavigationToggleIntent();
  const normalizedPath = normalizeTogglePath(pathname);

  if (!navigationIntent) return;

  if (!normalizedPath || navigationIntent.pathname === normalizedPath) {
    getSessionStorage()?.removeItem(TOGGLE_ROUTE_INTENT_KEY);
  }
};

/*
 * Finalizes route tracking after navigation completes by recording the current route
 * and clearing any matching pending toggle intent.
 * Example input: pathname="/analytics/v4/applications"
 * Example output: no return value; current-path tracking is updated in sessionStorage
 */
export const finalizeToggleRouteTracking = (pathname = "") => {
  const normalizedPath = normalizeTogglePath(pathname);

  if (!normalizedPath) return;

  setTrackedToggleCurrentPath(normalizedPath);
  clearNavigationToggleIntent(normalizedPath);
};

/*
 * Determines whether navigation into a new route should reset the toggle view.
 * Example input: previousPathname="/analytics/v4/reports", nextPathname="/analytics/v4/applications"
 * Example output: "card"
 */
export const getNavigationToggleView = (previousPathname = "", nextPathname = "") => {
  const previousPath = normalizeTogglePath(previousPathname);
  const nextPath = normalizeTogglePath(nextPathname);

  if (!previousPath || !nextPath || previousPath === nextPath) {
    return null;
  }

  if (isApplicationsPath(nextPath) && !isApplicationsPath(previousPath)) {
    return APPLICATION_TOGGLE_CONFIG.defaultValue;
  }

  if (
    isApp360Path(nextPath) &&
    (!isApp360Path(previousPath) || getApp360Name(previousPath) !== getApp360Name(nextPath))
  ) {
    return APP360_TOGGLE_CONFIG.defaultValue;
  }

  return null;
};

/*
 * Computes the toggle view for a navigation event and stores the pending intent
 * so the destination route can apply it after navigation completes.
 * Example input: previousPathname="/analytics/v4/reports", nextPathname="/analytics/v4/dashboards/Webex"
 * Example output: "map"
 */
export const prepareNavigationToggleView = (previousPathname = "", nextPathname = "") => {
  const nextToggleView = getNavigationToggleView(previousPathname, nextPathname);

  if (nextToggleView) {
    setNavigationToggleIntent(nextPathname, nextToggleView);
  } else {
    clearNavigationToggleIntent();
  }

  return nextToggleView;
};

/*
 * Returns a still-pending toggle view for the current route when navigation has
 * started but the route has not yet been marked as finalized.
 * Example input: pathname="/analytics/v4/dashboards/Webex"
 * Example output: "map" or null
 */
export const getPendingNavigationToggleView = (pathname = "") => {
  const normalizedPath = normalizeTogglePath(pathname);
  const navigationIntent = getNavigationToggleIntent();
  const trackedCurrentPath = getTrackedToggleCurrentPath();

  if (!navigationIntent || !normalizedPath) return null;

  if (
    navigationIntent.pathname === normalizedPath &&
    trackedCurrentPath !== normalizedPath
  ) {
    const config = getToggleConfig(normalizedPath);
    return config.allowedValues.includes(navigationIntent.toggleView)
      ? navigationIntent.toggleView
      : null;
  }

  return null;
};

/*
 * Returns the best initial toggle view for a route, preferring a pending
 * navigation intent over the persisted toggle value.
 * Example input: pathname="/analytics/v4/applications"
 * Example output: "card"
 */
export const getInitialToggleView = (pathname = "") =>
  getPendingNavigationToggleView(pathname) || getStoredToggleView(pathname);
